import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { notBlank } from "../../util/ext";
import { lensPath, lensProp, pick, set } from "ramda";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import {
  validateSalesAccountField,
  validateSalesAccount,
} from "../organizationValidation";
import Button from "@material-ui/core/Button";
import {
  useLibrarySalesAccountCreate,
  useLibrarySalesAccountUpdate,
} from "../organizationServices";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { useErrorBox } from "./use-errorBox";
import { KeyboardDatePicker } from "@material-ui/pickers";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const defaultSalesAccount = {
  nimbleAccountNumber: "",
  creatorEnabled: false,
  indieAuthorProjectEnabled: false,
  pressbooksEnabled: false,
  iapSubmissionUrl: "",
  authorWriterUrl: "",
  pressbooksSubmissionUrl: "",
  accountStartDate: null,
  accountRenewalDate: null,
  populationServed: null,
};

const EditLibrarySalesAccountDialog = ({
  open,
  organizationId,
  salesAccount,
  onCancel,
  onSave,
}) => {
  const classes = useStyles();

  const [fields, setFields] = useState({});
  useEffect(() => {
    if (open && notBlank(salesAccount)) {
      setFields(
        pick(
          [
            "nimbleAccountNumber",
            "creatorEnabled",
            "indieAuthorProjectEnabled",
            "pressbooksEnabled",
            "iapSubmissionUrl",
            "authorWriterUrl",
            "pressbooksSubmissionUrl",
            "accountStartDate",
            "accountRenewalDate",
            "cePurchaseAmount",
            "thirdPartyPurchase",
            "thirdPartyPurchaseAmount",
            "thirdPartyStartDate",
            "thirdPartyRenewalDate",
            "populationServed",
          ],
          salesAccount,
        ),
      );
    } else {
      setFields(defaultSalesAccount);
    }
  }, [open, salesAccount]);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleBlur = (field, value) => {
    const errors = validateSalesAccountField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => (event, value) => {
    const val = notBlank(value)
      ? value
      : notBlank(event)
      ? event.target.value
      : event;

    setFields(prev => set(lensPath(path), val, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const updateSalesAccount = useLibrarySalesAccountUpdate();
  const createSalesAccount = useLibrarySalesAccountCreate();
  const handleApply = useCallback(async () => {
    const validationErrors = validateSalesAccount(fields);
    console.log(validationErrors);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      try {
        if (notBlank(salesAccount)) {
          await updateSalesAccount.mutateAsync({
            resourceIds: { id: organizationId },
            data: fields,
          });
        } else {
          await createSalesAccount.mutateAsync({
            resourceIds: { id: organizationId },
            data: fields,
          });
        }
        onSave(fields);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [
    salesAccount,
    fields,
    organizationId,
    updateSalesAccount,
    createSalesAccount,
    onSave,
    setValidationErrors,
    setGeneralError,
  ]);

  return (
    <Dialog maxWidth="md" open={open} onClose={onCancel}>
      <DialogTitle id="edit-organization-detail-dialog-title">
        {notBlank(salesAccount)
          ? "Edit Library Sales Account"
          : "Add Library Sales Account"}
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item sm={12}>
            <TextField
              id="nimble-account-number"
              fullWidth
              label="Nimble Account Number"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.nimbleAccountNumber}
              onChange={handleTextChange(["nimbleAccountNumber"])}
              error={hasValidationError("nimbleAccountNumber")}
              helperText={firstValidationError("nimbleAccountNumber")}
              onBlur={() =>
                handleBlur("nimbleAccountNumber", fields.nimbleAccountNumber)
              }
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="population-served"
              fullWidth
              label="Population Served"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.populationServed}
              onChange={handleTextChange(["populationServed"])}
              error={hasValidationError("populationServed")}
              helperText={firstValidationError("populationServed")}
              onBlur={() =>
                handleBlur("populationServed", fields.populationServed)
              }
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.creatorEnabled}
                  onChange={handleCheckboxChange("creatorEnabled")}
                  name="creator-enabled"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Creator?"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.indieAuthorProjectEnabled}
                  onChange={handleCheckboxChange("indieAuthorProjectEnabled")}
                  name="indie-author-project-enabled"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Indie Author Project?"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.pressbooksEnabled}
                  onChange={handleCheckboxChange("pressbooksEnabled")}
                  name="pressbooks-enabled"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Pressbooks?"
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="iap-submission-url"
              fullWidth
              label="IAP Submission URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.iapSubmissionUrl}
              onChange={handleTextChange(["iapSubmissionUrl"])}
              error={hasValidationError("iapSubmissionUrl")}
              helperText={firstValidationError("iapSubmissionUrl")}
              onBlur={() =>
                handleBlur("iapSubmissionUrl", fields.iapSubmissionUrl)
              }
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="author-writer-url"
              fullWidth
              label="Author/Writer URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.authorWriterUrl}
              onChange={handleTextChange(["authorWriterUrl"])}
              error={hasValidationError("authorWriterUrl")}
              helperText={firstValidationError("authorWriterUrl")}
              onBlur={() =>
                handleBlur("authorWriterUrl", fields.authorWriterUrl)
              }
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="pressbooks-submission-url"
              fullWidth
              label="Pressbooks Submission URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.pressbooksSubmissionUrl}
              onChange={handleTextChange(["pressbooksSubmissionUrl"])}
              error={hasValidationError("pressbooksSubmissionUrl")}
              helperText={firstValidationError("pressbooksSubmissionUrl")}
              onBlur={() =>
                handleBlur(
                  "pressbooksSubmissionUrl",
                  fields.pressbooksSubmissionUrl,
                )
              }
            />
          </Grid>

          <Grid item sm={4}>
            <KeyboardDatePicker
              disableToolbar
              fullWidth
              autoOk
              id="account-start-date"
              format="MM/dd/yyyy"
              label="Account Start Date"
              variant="inline"
              value={fields.accountStartDate || null}
              onChange={handleTextChange(["accountStartDate"])}
              KeyboardButtonProps={{
                "aria-label": "change start date",
              }}
            />
          </Grid>

          <Grid item sm={4}>
            <KeyboardDatePicker
              disableToolbar
              fullWidth
              autoOk
              id="account-renewal-date"
              format="MM/dd/yyyy"
              label="Account Renewal Date"
              variant="inline"
              value={fields.accountRenewalDate || null}
              onChange={handleTextChange(["accountRenewalDate"])}
              KeyboardButtonProps={{
                "aria-label": "change start date",
              }}
            />
          </Grid>

          <Grid item sm={4}>
            <TextField
              id="ce-purchase-amount"
              fullWidth
              label="CE Purchase Amount"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.cePurchaseAmount}
              onChange={handleTextChange(["cePurchaseAmount"])}
              error={hasValidationError("cePurchaseAmount")}
              helperText={firstValidationError("cePurchaseAmount")}
              onBlur={() =>
                handleBlur("cePurchaseAmount", fields.cePurchaseAmount)
              }
            />
          </Grid>

          <Grid item sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.thirdPartyPurchase}
                  onChange={handleCheckboxChange("thirdPartyPurchase")}
                  name="third-party-purchase"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Third Party Purchase?"
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="third-party-purchase-amount"
              fullWidth
              label="Third Party Purchase Amount"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.thirdPartyPurchaseAmount}
              onChange={handleTextChange(["thirdPartyPurchaseAmount"])}
              error={hasValidationError("thirdPartyPurchaseAmount")}
              helperText={firstValidationError("thirdPartyPurchaseAmount")}
              onBlur={() =>
                handleBlur(
                  "thirdPartyPurchaseAmount",
                  fields.thirdPartyPurchaseAmount,
                )
              }
            />
          </Grid>

          <Grid item sm={6}>
            <KeyboardDatePicker
              disableToolbar
              fullWidth
              autoOk
              id="third-party-start-date"
              format="MM/dd/yyyy"
              label="Third Party Start Date"
              variant="inline"
              value={fields.thirdPartyStartDate || null}
              onChange={handleTextChange(["thirdPartyStartDate"])}
              KeyboardButtonProps={{
                "aria-label": "change start date",
              }}
            />
          </Grid>

          <Grid item sm={6}>
            <KeyboardDatePicker
              disableToolbar
              fullWidth
              autoOk
              id="third-party-renewal-date"
              format="MM/dd/yyyy"
              label="Third Party Renewal Date"
              variant="inline"
              value={fields.thirdPartyRenewalDate || null}
              onChange={handleTextChange(["thirdPartyRenewalDate"])}
              KeyboardButtonProps={{
                "aria-label": "change start date",
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLibrarySalesAccountDialog;
